import api from '@/redux/api/sellerCatalogs';
import { createSelector } from 'reselect';
import difference from 'lodash/difference';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import ms from 'ms';
import union from 'lodash/union';

export const LOAD_SELLER_UPCOMING_CATALOGS_FAIL = 'la/domain/sellerUpcomingCatalogs/LOAD_FAIL';
export const LOAD_SELLER_UPCOMING_CATALOGS_REQUEST = 'la/domain/sellerUpcomingCatalogs/LOAD_REQUEST';
export const LOAD_SELLER_UPCOMING_CATALOGS_SUCCESS = 'la/domain/sellerUpcomingCatalogs/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('30m');

const DEFAULT_STATE = {
    byId: {},
    loaded: {},
    loading: [],
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case LOAD_SELLER_UPCOMING_CATALOGS_FAIL:
            return {
                ...state,
                loading: difference(state.loading, [action.meta.sellerId]),
            };
        case LOAD_SELLER_UPCOMING_CATALOGS_REQUEST:
            return {
                ...state,
                loading: union(state.loading, [action.payload]),
            };
        case LOAD_SELLER_UPCOMING_CATALOGS_SUCCESS:
            const { sellerId } = action.meta;
            return {
                ...state,
                byId: {
                    ...state.byId,
                    [sellerId]: action.payload.catalogs.map((catalog) => catalog.catalogId),
                },
                loaded: { ...state.loaded, [sellerId]: action.meta.actionTime },
                loading: difference(state.loading, [sellerId]),
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.sellerUpcomingCatalogs;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, (state) => state.byId);

const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

const loadingSelector = createSelector(stateSelector, (state) => state.loading);

// takes in a (state, id) and returns the data
export const getSellerUpcomingCatalogs = createSelector([byIdSelector, idSelector], (byId, id) => byId[id] || []);

// takes in a (state, id) and returns the load time
export const getLoadTimeForSellerUpcomingCatalogs = createSelector(
    [loadedSelector, idSelector],
    (loaded, id) => loaded[id] || 0
);

// takes in a (state, id) and returns the is loading
export const isSellerUpcomingCatalogsLoading = createSelector([loadingSelector, idSelector], (loading, id) => {
    return loading.includes(id);
});

const shouldFetchUpcomingCatalogs = (state, sellerId) => {
    if (!sellerId) {
        return false;
    }
    const item = getSellerUpcomingCatalogs(state, sellerId);
    if (item.length) {
        const loaded = getLoadTimeForSellerUpcomingCatalogs(state, sellerId);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) {
            return false;
        }
    }
    const loading = isSellerUpcomingCatalogsLoading(state, sellerId);
    return !loading;
};

/* ACTION CREATORS */
const loadUpcomingCatalogs = (sellerId) => async (dispatch, getState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        dispatch({
            payload: sellerId,
            type: LOAD_SELLER_UPCOMING_CATALOGS_REQUEST,
        });
        const response = await api.fetchSellerUpcomingCatalogsById({
            authToken,
            deployment,
            sellerId,
        });
        dispatch({
            meta: { actionTime: Date.now(), sellerId },
            payload: response.data,
            type: LOAD_SELLER_UPCOMING_CATALOGS_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            meta: { sellerId },
            payload: error,
            type: LOAD_SELLER_UPCOMING_CATALOGS_FAIL,
        });
    }
};

export const fetchSellerUpcomingCatalogsIfNeeded = (sellerId: number) => async (
    dispatch: Function,
    getState: Function
) => {
    if (shouldFetchUpcomingCatalogs(getState(), sellerId)) {
        return dispatch(loadUpcomingCatalogs(sellerId));
    }
    return Promise.resolve();
};
